import React from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Package, Euro, Link2 } from "lucide-react";
import { formatNumber } from "../../../helpers/string";

interface ReferenceCardProps {
  id: number;
  name: string;
  url: string;
  imgUrl: string;
  reference: string;
  lastPrice: number;
  lastQuantity: number;
  productsCount?: number;
}

export const ReferenceCard: React.FC<ReferenceCardProps> = ({
  id,
  name,
  url,
  imgUrl,
  reference,
  lastPrice,
  lastQuantity,
  productsCount = 0,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/reference/${id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
    >
      <div className="p-4">
        {/* Product Image */}
        <div className="flex justify-center mb-3">
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
            {imgUrl ? (
              <img
                src={imgUrl}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Package size={32} className="text-gray-400" />
            )}
          </div>
        </div>

        {/* Reference Name */}
        <h3 className="text-center text-sm font-semibold text-gray-900 mb-1 truncate">
          {name}
        </h3>

        {/* Reference Code */}
        {reference && (
          <p className="text-center text-xs text-gray-500 mb-2 truncate">
            Réf: {reference}
          </p>
        )}

        {/* Price and Quantity */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-emerald-50 rounded-lg p-2">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Euro size={12} className="text-emerald-600" />
            </div>
            <p className="text-xs font-bold text-emerald-700 text-center">
              {formatNumber(lastPrice)} €
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg p-2">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Package size={12} className="text-blue-600" />
            </div>
            <p className="text-xs font-bold text-blue-700 text-center">
              {formatNumber(lastQuantity)} pcs
            </p>
          </div>
        </div>

        {/* URL */}
        <div className="flex items-center justify-center gap-1 mb-3">
          <ExternalLink size={12} className="text-gray-400" />
          <p className="text-xs text-gray-500 truncate max-w-[200px]">{url}</p>
        </div>

        {/* Linked Products Count */}
        <div className="flex items-center justify-center gap-2 pt-3 border-t border-gray-100">
          <Link2 size={14} className="text-gray-400" />
          <span className="text-xs font-medium text-gray-600">
            {productsCount} produit{productsCount !== 1 ? "s" : ""} lié
            {productsCount !== 1 ? "s" : ""}
          </span>
        </div>
      </div>
    </div>
  );
};
